import React from 'react';
import { Link } from 'react-router-dom';
interface ContractorSearchResultsCardProps {
  id: string;
  name: string;
  companyName: string;
  rating: number;
  reviews: number;
  imageUrl: string;
  isVerified: boolean;
  price: number;
  priceUnit: string;
  location: string;
  experience: string;
  availability?: string;
  specialization: string;
  tier?: string;
  isMobile: boolean;
  isDesktop: boolean;
  emergencyService?: boolean;
  insuredAndBonded?: boolean;
}

const ContractorSearchResultsCard: React.FC<ContractorSearchResultsCardProps> = ({
  id,
  name,
  companyName,
  rating,
  reviews,
  imageUrl,
  isVerified,
  price,
  priceUnit,
  location,
  experience,
  availability,
  specialization,
  tier,
  isMobile,
  isDesktop,
  emergencyService,
  insuredAndBonded,
}) => {
  const renderStars = (count: number) => {
    const stars = [];
    for (let i = 0; i < 5; i++) {
      if (i < Math.floor(count)) {
        stars.push(<span key={i} className="material-symbols-outlined text-[18px]" data-icon="star" data-weight="fill" style={{ fontVariationSettings: "'FILL' 1" }}>star</span>);
      } else if (i === Math.floor(count) && count % 1 !== 0) {
        stars.push(<span key={i} className="material-symbols-outlined text-[18px]" data-icon="star_half" data-weight="fill" style={{ fontVariationSettings: "'FILL' 1" }}>star_half</span>);
      } else {
        stars.push(<span key={i} className="material-symbols-outlined text-[18px]" data-icon="star" data-weight="fill">star</span>);
      }
    }
    return stars;
  };

  return (
    <div className="bg-surface-container-lowest rounded-xl p-4 shadow-sm border border-outline-variant flex gap-4 active:scale-[0.98] transition-transform md:bg-surface-container-lowest md:rounded-xl md:p-6 md:shadow-sm md:border md:border-transparent md:hover:border-outline-variant/50 md:transition-all md:flex md:flex-col md:md:flex-row md:gap-gutter md:relative">
      {isVerified && (
        <div className="absolute top-4 right-4 flex gap-2">
          <div className="bg-emerald-100 text-emerald-800 px-3 py-1 rounded-full flex items-center gap-1 text-[11px] font-bold uppercase tracking-wider">
            <span className="material-symbols-outlined text-[14px]" data-icon="verified" data-weight="fill">verified</span>
            Verified
          </div>
          {tier && (
            <div className="bg-primary-container text-on-primary-container px-3 py-1 rounded-full text-[11px] font-bold uppercase tracking-wider">{tier}</div>
          )}
        </div>
      )}
      <div className="relative shrink-0 w-20 h-20 md:w-48 md:h-48">
        <img alt={name} className="w-full h-full object-cover rounded-lg shadow-inner" src={imageUrl} />
      </div>
      <div className="flex-1 flex flex-col justify-between">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <h3 className="font-headline-sm text-[16px] text-primary">{companyName || name}</h3>
            <div className="flex items-center text-amber-500">
              {renderStars(rating)}
              <span className="font-label-bold text-label-bold ml-1">{rating.toFixed(1)}</span>
              <span className="text-on-surface-variant font-label-md text-label-md ml-1">({reviews} reviews)</span>
            </div>
          </div>
          <p className="font-label-bold text-label-bold text-secondary uppercase tracking-widest mb-3">{specialization}</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-y-2 gap-x-4 mb-4">
            <div className="flex items-center gap-2 text-on-surface-variant">
              <span className="material-symbols-outlined text-[18px]" data-icon="location_on">location_on</span>
              <span className="font-body-md text-body-md">{location}</span>
            </div>
            <div className="flex items-center gap-2 text-on-surface-variant">
              <span className="material-symbols-outlined text-[18px]" data-icon="history">history</span>
              <span className="font-body-md text-body-md">{experience}</span>
            </div>
            {availability && (
              <div className="flex items-center gap-2 text-on-surface-variant">
                <span className="material-symbols-outlined text-[18px]" data-icon="calendar_today">calendar_today</span>
                <span className="font-body-md text-body-md">{availability}</span>
              </div>
            )}
            {emergencyService && (
              <div className="flex items-center gap-2 text-on-surface-variant text-error font-bold">
                <span className="material-symbols-outlined text-[18px]" data-icon="bolt">bolt</span>
                <span className="font-body-md text-body-md">24/7 Emergency Service</span>
              </div>
            )}
            {insuredAndBonded && (
              <div className="flex items-center gap-2 text-on-surface-variant">
                <span className="material-symbols-outlined text-[18px]" data-icon="check_circle">check_circle</span>
                <span className="font-body-md text-body-md">Insured &amp; Bonded</span>
              </div>
            )}
          </div>
        </div>
        <div className="flex items-center justify-end border-t border-outline-variant pt-4">
          <Link to={`/contractors/${id}`} className="bg-primary text-on-primary px-8 py-3 rounded-lg font-label-bold text-label-bold hover:bg-primary-container hover:text-on-primary-container transition-all active:scale-95 shadow-md">
            View Details
          </Link>
        </div>
      </div>
    </div>
  );
};

export default ContractorSearchResultsCard;
